<template>
  <main>
    <div v-if="loadFailedError">{{ loadFailedError }}</div>
    <WCMobileSubpageHeader v-if="isMobile && hasBreadcrumb" :title="resultQuery" />
    <div v-if="isMobile && hasBreadcrumb" />
    <WCBreadcrumb v-if="hasBreadcrumb" :crumbs="crumbs" @selected="breadcrumbSelected" />
    <WCSpinner v-if="!loadFailedError && loading" position="center" size="xl" />
    <div v-else-if="!items.length">
      <h2 class="text-center" aria-level="1" tabindex="-1" ref="focusFirst">
        {{ $t('noSearchResultsFor', { name: resultQuery }) }}
      </h2>
      <PlatformRenderer>
        <template v-slot:onDesktop>
          <AlternateSearchTerms
            :alternates="alternates"
            :direction="alternateSearchTermsDirectionColumn"
          />
        </template>
        <template v-slot:onMobile>
          <AlternateSearchTerms
            :alternates="alternates"
            :direction="alternateSearchTermsDirectionColumn"
            :partialCenter="true"
          />
        </template>
      </PlatformRenderer>
    </div>
    <template v-else>
      <platform-renderer>
        <template v-slot:onDesktop></template>
        <template v-slot:onMobile>
          <div>
            <div class="row">
              <div class="col-12">
                <WCCollapseCard
                  v-if="sortingOptions"
                  :isCollapsed="isSortOptionCollapsed"
                  headerClass="sort-by-title"
                  :aria-label="$t('sortProductBy')"
                  openIcon="chevron-down"
                  closeIcon="chevron-up"
                  @onCollapseOrExpand="onSortCollapseChanged"
                >
                  <template v-slot:headerTitle>
                    <label class="mb-0" id="sort_options"
                      >{{ $t('sort') }}: {{ getSortedValue }}</label
                    >
                  </template>
                  <template v-slot:cardBody>
                    <div class="wc-wrapper-scroll overflow-auto pl-4 pr-4">
                      <div
                        class="row justify-content-between py-1"
                        v-for="(opt, oi) in sortingOptions"
                        :key="oi"
                      >
                        <div class="wc-filter-checkbox">
                          <WCRadioButton
                            class="align-items-center"
                            name="sortBy"
                            :id="'sort-by-' + opt.value"
                            :modelValue="'sort-by-' + s"
                            @update:modelValue="() => mobileSortChanged(opt.value)"
                          >
                            {{ opt.name }}
                          </WCRadioButton>
                        </div>
                      </div>
                    </div>
                  </template>
                </WCCollapseCard>
              </div>
            </div>
          </div>
        </template>
      </platform-renderer>
      <div class="d-flex flex-column top-filter-section">
        <div class="d-flex justify-content-between align-items-center">
          <h2
            v-if="!loadFailedError && !loading"
            aria-level="1"
            tabindex="-1"
            ref="focusFirst"
            class="d-none d-lg-block text-primary"
          >
            {{ $t('showingResultsFor') }}
            {{ $t('browseResultQuery', { browseResult: resultQuery }) }}
          </h2>
          <span />
          <platform-renderer>
            <template v-slot:onDesktop>
              <div class="wc-filter-wrapper">
                <div class="mr-5 count-result">{{ totalCount }} {{ $t('results') }}</div>
                <form>
                  <div class="form-group" v-if="sortingOptions">
                    <label for="search-sort" class="h5 font-weight-bold">{{ $t('sort') }}:</label>
                    <WCSelect
                      v-model="s"
                      @update:modelValue="sortChanged"
                      :aria-label="$t('sortProductBy')"
                    >
                      <option
                        v-for="(opt, oi) in sortingOptions"
                        :key="oi"
                        :label="opt.name"
                        :value="opt.value"
                        :selected="opt.value === s"
                        >{{ opt.name }}</option
                      >
                    </WCSelect>
                  </div>
                </form>
              </div>
            </template>
            <template v-slot:onMobile></template>
          </platform-renderer>
        </div>
        <PlatformRenderer>
          <template v-slot:onDesktop>
            <AlternateSearchTerms :alternates="alternates" />
          </template>
          <template v-slot:onMobile>
            <AlternateSearchTerms
              :alternates="alternates"
              :direction="alternateSearchTermsDirectionColumn"
              :partialCenter="true"
            />
          </template>
        </PlatformRenderer>
      </div>
      <div class="mb-4 wc-page-wrapper">
        <platform-renderer>
          <template v-slot:onMobile>
            <div v-if="!loadFailedError && !loading" class="wc-filter-bar pb-2 pt-2">
              <div class="d-flex justify-content-between align-items-start">
                <div class="w-75">
                  <span v-if="$route.query.q">
                    {{ $t('showingResultsFor') }} {{ $route.query.q }}
                  </span>
                  <div class="my-2 font-weight-light font-size-sm">
                    {{ totalCount }} {{ $t('results') }}
                  </div>
                </div>
                <button @click="showFiltersChanged" class="btn text-primary float-right p-0">
                  <font-awesome-icon icon="sliders" />
                  {{ $t('filters') }}
                </button>
              </div>
              <div v-show="showMobileFilters" class="wc-mobile-filter-pop">
                <WCMobileSearchFilter
                  v-model:facets="facets"
                  @update:facets="facetChanged"
                  v-on:close-filters="showFiltersChanged"
                  :groupChildren="groupChildren"
                  @groupChanged="groupChanged"
                  :crumbs="crumbs"
                  @specificCategoryMobileChip="onMobileCategoryChipClicked"
                  @clearSpecificCategories="onClearSpecificCategories"
                />
              </div>
            </div>
          </template>

          <template v-slot:onDesktop>
            <div v-if="!loadFailedError && !loading" class="mt-4 mr-4 wc-left-col">
              <!-- START: Text Icon Chips common for both CategoryFilter and SearchFilter -->
              <div role="list" class="row wc-selected-header ml-0 mb-3">
                <!-- START: Specific Categories Text Icon Chip -->
                <span role="listitem" v-for="(group, index) in crumbs" :key="index + group.label">
                  <div v-if="group.label.toLowerCase() !== 'all items'">
                    <WCTextIconChip
                      :text="group.label"
                      :id="group.label"
                      @click="onSpecificCategoryChip(group)"
                      class="mt-2 mr-2 font-size-sm"
                    />
                  </div>
                </span>
                <!-- END: Specific Categories Text Icon Chip -->

                <!-- START: All Categories Text Icon Chip -->
                <template v-if="filteredSelectedValues && filteredSelectedValues.length">
                  <template v-for="category in filteredSelectedValues">
                    <span role="listitem" v-for="value in category.values" :key="value.name">
                      <WCTextIconChip
                        :text="value.name"
                        v-if="value.selected"
                        :id="value.name"
                        @click="onAllCategoryChip(category, value)"
                        class="mt-2 mr-2 font-size-sm"
                      />
                    </span>
                  </template>
                </template>
                <!-- END: All Categories Text Icon Chip  -->
              </div>
              <!-- END: Text Icon Chips common for both CategoryFilter and SearchFilter -->

              <WCCategoriesFilter
                v-if="groupChildren.length > 0"
                :groupChildren="groupChildren"
                @groupChanged="groupChanged"
              />
              <WCSearchFilter
                v-model:facets="facets"
                ref="searchFilter"
                :itemSearchInput="categoryItemSearchInputData"
                :itemSearchCategory="categoryFilterName"
                @update:facets="facetChanged"
                @categoryFiltered="filteredCategoryValues"
                @categoryItemSearchInput="onCategoryItemSearch"
              />
            </div>
          </template>
        </platform-renderer>
        <div class="mt-4 wc-right-col">
          <div v-if="!loadFailedError && !loading">
            <!-- START : Browse Banner Img -->
            <WCImage
              v-if="browseBannerImg"
              :imageSrc="`${$configuration.cdn}/api/img/${browseBannerImg}`"
              imageClass="wc-browse-banner-img mb-4"
              :imageAlt="$route.query.q ? $route.query.q : crumbs[crumbs.length - 1].label"
              defaultClass="font-size-7xl"
            />
            <!-- END : Browse Banner Img -->
            <platform-renderer>
              <template v-slot:onMobile>
                <WCProductCardGroup class="mobile-product-card-group" role="list">
                  <WCMobileProductCard
                    class="p-1"
                    v-for="item in pageList"
                    :key="item.key"
                    :item="item"
                    role="listitem"
                  />
                </WCProductCardGroup>
              </template>

              <template v-slot:onDesktop>
                <WCProductCardGroup role="list">
                  <WCProductCard
                    v-for="item in pageList"
                    :key="item.key"
                    :item="item"
                    role="listitem"
                  />
                </WCProductCardGroup>
              </template>
            </platform-renderer>
          </div>
          <div
            class="wc-pagination-wrapper d-flex flex-column align-items-center flex-md-row justify-content-md-between m-1 py-4"
            :class="{ 'sr-only': loading }"
          >
            <platform-renderer>
              <template v-slot:onMobile>
                <WCPagination
                  class="m-0"
                  :collectionSize="totalCount"
                  v-model="pn"
                  @update:modelValue="pnChanged"
                  :pageSize="Number(ps)"
                  :maxSize="7"
                  :page-links="false"
                />
              </template>
              <template v-slot:onDesktop>
                <WCPagination
                  class="m-0"
                  :collectionSize="totalCount"
                  v-model="pn"
                  @update:modelValue="pnChanged"
                  :pageSize="Number(ps)"
                  :maxSize="7"
                />
              </template>
            </platform-renderer>

            <platform-renderer>
              <template v-slot:onMobile>
                <div class="text-center" aria-live="assertive">
                  {{ $t('showing') }} {{ startRecord }} {{ $t('to') }} {{ endRecord }}
                  {{ $t('of') }} {{ totalCount }} {{ $t('results') }}
                  <span class="sr-only">{{ $t('forText', { name: resultQuery }) }}</span> ({{
                    pagesAvailable
                  }}
                  {{ $t('pages') }})
                </div>
              </template>
              <template v-slot:onDesktop>
                <span class="text-right" aria-live="assertive">
                  {{ $t('showing') }} {{ startRecord }} {{ $t('to') }} {{ endRecord }}
                  {{ $t('of') }} {{ totalCount }} {{ $t('results') }}
                  <span class="sr-only">{{ $t('forText', { name: resultQuery }) }}</span> ({{
                    pagesAvailable
                  }}
                  {{ $t('pages') }})
                </span>
              </template>
            </platform-renderer>
          </div>
          <div v-if="!isScanAndGoMode">
            <platform-renderer>
              <template v-slot:onMobile>
                <div aria-live="assertive">
                  <WCSpecialOrderButton
                    v-if="$configuration.allowSpecialOrder"
                    class="btn btn-primary btn-block mb-3"
                    style="height: 40px;"
                  />
                </div>
              </template>
              <template v-slot:onDesktop>
                <div aria-live="assertive">
                  <WCSpecialOrderButton
                    v-if="$configuration.allowSpecialOrder"
                    class="btn btn-primary btn-block mb-3"
                    style="max-width: 240px; float: right;"
                  />
                </div>
              </template>
            </platform-renderer>
          </div>
        </div>
      </div>
    </template>
  </main>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import WCSelect from '@/modules/forms/components/WCSelect/WCSelect.vue';
import PlatformRenderer from '@/modules/platform/components/PlatformRenderer';
import WCMobileSearchFilter from '@/modules/mobileSearch/components/WCMobileSearchFilter/WCMobileSearchFilter.vue';
import FocusMixin from '@/mixins/FocusMixin';
import WCTextIconChip from '@/components/WCTextIconChip/WCTextIconChip.vue';
import WCImage from '@/components/WCImage/WCImage.vue';
import WCCollapseCard from '@/components/WCCollapseCard/WCCollapseCard.vue';
import WCRadioButton from '@/modules/forms/components/WCRadioButton/WCRadioButton.vue';
import WCSpecialOrderButton from '@/modules/cart/components/WCSpecialOrderButton/WCSpecialOrderButton.vue';
import AnalyticsService from '@/services/analytics.service';
import TrackableEventConstants from '@/constants/TrackableEventConstants';
import WCMobileSubpageHeader from '@/components/WCMobileSubpageHeader/WCMobileSubpageHeader.vue';
import { PlatformMixin } from '@/modules/platform';
import WCSearchFilter from '../../components/WCSearchFilter/WCSearchFilter.vue';
import WCProductCardGroup from '../../components/WCProductCardGroup/WCProductCardGroup.vue';
import WCMobileProductCard from '../../components/WCMobileProductCard/WCMobileProductCard.vue';
import WCProductCard from '../../components/WCProductCard/WCProductCard.vue';
import WCBreadcrumb from '../../components/WCBreadcrumb/WCBreadcrumb.vue';
import WCPagination from '../../components/WCPagination/WCPagination.vue';
import WCSpinner from '../../components/WCSpinner/WCSpinner.vue';
import WCCategoriesFilter from '../../components/WCCategoriesFilter/WCCategoriesFilter.vue';
import AlternateSearchTerms, {
  ALTERNATE_SEARCH_TERMS_DIRECTION,
} from './components/AlternateSearchTerms/AlternateSearchTerms.vue';

const SIZE_OPTIONS = [8, 16, 32, 64];

export default {
  name: 'Browse',
  components: {
    PlatformRenderer,
    WCMobileProductCard,
    WCSearchFilter,
    WCMobileSearchFilter,
    WCProductCardGroup,
    WCProductCard,
    WCBreadcrumb,
    WCPagination,
    WCSelect,
    WCSpinner,
    WCCategoriesFilter,
    WCTextIconChip,
    WCImage,
    WCCollapseCard,
    WCRadioButton,
    WCSpecialOrderButton,
    AlternateSearchTerms,
    WCMobileSubpageHeader,
  },
  mixins: [FocusMixin, PlatformMixin], // Mixin for main content focus via routing
  props: {
    isScanAndGoMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      loadFailedError: null,
      alternates: [],
      items: [],
      facets: [],
      crumbs: [{ label: 'All Items', data: { group: 'First crumb' } }],
      ps: 32,
      s: 'string:',
      pn: 1,
      totalCount: 0,
      groupPath: [],
      groupChildren: [],
      showMobileFilters: false,
      categoryItemSearchInputData: '',
      categoryFilterName: '',
      filteredSelectedValues: '',
      isSortOptionCollapsed: true,
    };
  },
  methods: {
    /**
     * method triggered on clicking specifc category icon chip - mobile search filter
     */
    onMobileCategoryChipClicked(group) {
      this.onSpecificCategoryChip(group);
    },
    /**
     * method triggered on clciking clear filteres in specific category selection - mobile search filter
     */
    onClearSpecificCategories() {
      this.breadcrumbSelected(this.crumbs[0]);
    },
    /**
     * method triggered on clicking specific category icon chip - desktop search filter
     */
    onSpecificCategoryChip(selectedGroup) {
      const currentSelectedIndex = this.crumbs.findIndex(
        crumbItem => crumbItem.data.group === selectedGroup.data.group,
      );
      const previousCrumb = this.crumbs[currentSelectedIndex - 1];
      this.breadcrumbSelected(previousCrumb);
    },
    /**
     * method triggered on clicking all category text icon chip
     */
    onAllCategoryChip(category, value) {
      this.$refs.searchFilter.onChange(category, value, false);
    },
    showFiltersChanged() {
      this.showMobileFilters = !this.showMobileFilters;
    },
    pnChanged(pageNumber) {
      this.$router.push({
        name: 'Browse',
        query: { ...this.$route.query, pn: pageNumber === 1 ? undefined : pageNumber },
      });
    },
    mobileSortChanged(sortOption) {
      this.isSortOptionCollapsed = true;
      this.sortChanged(sortOption);
    },
    onSortCollapseChanged(event) {
      this.isSortOptionCollapsed = event;
    },
    sortChanged(sortOption) {
      this.s = sortOption;
      this.$router.push({
        name: 'Browse',
        query: { ...this.$route.query, s: sortOption === '' ? undefined : sortOption },
      });
    },
    facetChanged(newFacets) {
      const facets = {};

      newFacets.forEach(c => {
        const selected = c.values.filter(v => v.selected);
        if (selected.length > 0) {
          facets[c.key] = selected.map(v => v.key);
        } else {
          facets[c.key] = undefined;
        }
      });

      // Reset pagination
      const prevQuery = this.$route.query;
      prevQuery.pn = undefined;
      this.$router.push({
        name: 'Browse',
        query: { ...prevQuery, ...facets },
      });
    },
    groupChanged(groupSelected) {
      // If only one value in g, then it is string. If more than one value, then it is an array
      const oldGs =
        typeof this.$route.query.g === 'string' ? [this.$route.query.g] : this.$route.query.g;
      this.$router.push({
        name: 'Browse',
        query: { ...this.$route.query, g: [...oldGs, groupSelected.id] },
      });
    },
    breadcrumbSelected(crumb) {
      if (crumb.label && crumb.label.toLowerCase() === 'all items') {
        this.$router.push({
          name: 'Browse',
          query: { ...this.$route.query, g: undefined },
        });
      } else if (this.$route.query.g.includes(crumb.data.group)) {
        const selectedIndex = this.$route.query.g.indexOf(crumb.data.group) + 1;
        const groups = this.$route.query.g.slice(0, selectedIndex);
        this.$router.push({
          name: 'Browse',
          query: { ...this.$route.query, g: groups },
        });
      } else {
        console.log('Error selecting breadcrumb');
      }
    },
    /**
     * Method to set the filteredSelectedValues for All Categories Text Icon Chip
     */
    filteredCategoryValues(categorySelectedValues) {
      if (categorySelectedValues) this.filteredSelectedValues = categorySelectedValues;
    },
    /**
     * Set the category item search input value and category name
     */
    onCategoryItemSearch(categoryItemSearchInput, categoryName) {
      this.categoryItemSearchInputData = categoryItemSearchInput;
      this.categoryFilterName = categoryName;
    },
    async browse() {
      this.loading = true;
      this.loadFailedError = null;
      const { pn = 1, ps = 32, s = '', g = [] } = this.$route.query;

      const ignoredFacetParams = ['g', 'pn', 'ps', 'q', 'iq', 's', 'g0', 'g1', 'g2', 'g3'];
      const reqFacets = {};

      const entries = Object.entries(this.$route.query);
      entries.forEach(param => {
        if (ignoredFacetParams.indexOf(param[0]) < 0) {
          // MY main worry with this is what if they entered a facet into URL that is not actually facet
          const [key, value] = param;
          reqFacets[key] = value;
        }
      });

      try {
        const response = await axios.post('api/b/', {
          ...this.$route.query,
          facets: reqFacets,
          pn,
          ps,
          s,
          g,
        });

        this.alternates = response.data.alternates;
        this.items = response.data.items;
        this.facets = response.data.facets;
        this.totalCount = response.data.totalCount;

        this.groupPath = response.data.groupPath;
        this.crumbs = [{ label: 'All Items', data: { group: 'First crumb' } }];
        this.groupPath.forEach(group => {
          this.crumbs.push({ label: group.name, data: { group: group.id } });
        });

        this.groupChildren = response.data.groupChildren;
        AnalyticsService.track(TrackableEventConstants.Search, {
          query: this.$route.query.q,
          items: this.pageList,
          customerId: this.getUser?.sub,
        });

        AnalyticsService.track(TrackableEventConstants.ItemImpression, {
          items: this.pageList,
          listId: -1,
          listName: 'Browse',
          customerId: this.getUser?.sub,
        });
      } catch (error) {
        this.loading = false;
        this.loadFailedError = error;
        console.error(error);
      }

      this.loading = false;

      this.pn = Number(pn);
      this.ps = Number(ps);
      this.s = s;
    },
  },
  created() {
    this.SIZE_OPTIONS = SIZE_OPTIONS;
    this.browse();
  },
  watch: {
    $route: 'browse', // Anytime $router is used, the browse method is called
  },

  computed: {
    ...mapGetters({
      getUser: 'user/getUser',
    }),
    /**
     * Search result query property
     */
    resultQuery() {
      if (this.$route.query.q) return this.$route.query.q;
      return this.crumbs[this.crumbs.length - 1].label;
    },
    pageList() {
      return this.items.slice(0, this.ps);
    },
    startRecord() {
      return this.totalCount > 0 ? (this.pn - 1) * this.ps + 1 : 0;
    },
    endRecord() {
      return Math.min(this.totalCount, this.pn * this.ps);
    },
    pagesAvailable() {
      return Math.ceil(this.totalCount / this.ps);
    },
    /**
     * Method to get the query key and value
     */
    getSRPageQuery() {
      let queryString = '';
      Object.keys(this.$route.query).forEach(query => {
        if (this.$route.query[query]) queryString += `${query} ${this.$route.query[query]} `;
      });
      return queryString;
    },
    hasBreadcrumb() {
      return this.groupPath.length > 0;
    },
    /**
     * Computed property to return browser banner image
     * To display the currently selected banner image (item group menu/item group)
     */
    browseBannerImg() {
      const itemGroupLength = this.groupPath && this.groupPath.length;
      const itemGroupImage =
        this.groupPath &&
        this.groupPath[itemGroupLength - 1] &&
        this.groupPath[itemGroupLength - 1].image;

      return itemGroupImage;
    },
    sortingOptions() {
      // RecSys items not yet sortable WC-763
      if (
        this.groupPath &&
        (this.groupPath[0]?.id.startsWith('REC') || this.groupPath[0]?.id.startsWith('FBI'))
      ) {
        return null;
      }

      return [
        { name: 'Default', value: '' },
        { name: 'Most Popular', value: 'POP' },
        { name: 'Name (A - Z)', value: 'NAZ' },
        { name: 'Name (Z - A)', value: 'NZA' },
        { name: 'Price (High - Low)', value: 'PHL' },
        { name: 'Price (Low - High)', value: 'PLH' },
      ];
    },
    getSortedValue() {
      if (!this.sortingOptions || this.s === undefined) return '';
      return this.sortingOptions.find(sort => sort.value === this.s)?.name || '';
    },
    alternateSearchTermsDirectionColumn() {
      return ALTERNATE_SEARCH_TERMS_DIRECTION.COLUMN;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.top-filter-section {
  border-bottom: 3px solid var(--secondary, $secondary);
}

@include media-breakpoint-down(xs) {
  .top-filter-section {
    border: 0;
    margin-top: 0;
  }
}

@include media-breakpoint-down(md) {
  .wc-page-wrapper {
    display: block;
  }
  .top-filter-section {
    margin-top: 0.5rem;
  }

  .wc-left-col {
    display: none;
  }
}

@include media-breakpoint-up(lg) {
  .wc-page-wrapper {
    display: flex;
    justify-content: space-between;
    min-height: rem(500px);
  }
}

.wc-left-col {
  min-width: 20%;
  max-width: rem(300px);
}

.wc-right-col {
  flex-grow: 1;
}

.wc-filter-wrapper {
  display: flex;
  justify-content: right;
  align-items: center;
}

.wc-pagination-wrapper {
  display: flex;
  justify-content: space-between;
}

.wc-filter-bar {
  font-weight: 500;
}
.wc-mobile-filter-pop {
  position: fixed;
  overflow-y: auto;
  background: var(--white);
  z-index: 3000; /* map-get($zindex, 'modals');*/
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.count-result {
  line-height: 1;
}

.wc-browse-banner-img {
  max-height: 15.5rem;
  max-width: 100%;
  height: auto;
  object-fit: contain;
}
</style>
